import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CreditCard,
  TrendingUp,
  DollarSign,
  Shield,
  Clock,
  CheckCircle,
  ArrowRight,
  Smartphone,
  PiggyBank,
  Target,
  Award,
  Users,
  Home,
  Car,
  GraduationCap,
  Heart,
  Star
} from 'lucide-react';

// Import professional images
import businessPeopleImage from '../assets/middle-aged-hispanic-business-people.jpg';
import consultationImage from '../assets/afro-american-man-suit-holding-documents-talking-female-boss.jpg';
import happyCustomersImage from '../assets/two-indian-business-man-suits-sitting-office-cafe-looking-laptop-drinking-coffee.jpg';
import familyPlanningImage from '../assets/cheerful-friends-strolling-street-with-smartphones.jpg';

const PersonalBanking = () => {
  const accountTypes = [
    {
      icon: CreditCard,
      title: "Checking Accounts",
      description: "Everyday banking made simple and convenient",
      features: [
        "No minimum balance required",
        "Free online banking and mobile app",
        "Unlimited transactions",
        "Free debit card with contactless payments",
        "Overdraft protection available"
      ],
      startingBalance: "$0",
      monthlyFee: "$0",
      bgColor: "from-blue-500 to-blue-600",
      popular: false
    },
    {
      icon: TrendingUp,
      title: "High-Yield Savings",
      description: "Grow your money with competitive interest rates",
      features: [
        "Competitive interest rates up to 4.50% APY",
        "No monthly maintenance fees",
        "FDIC insured up to $250,000",
        "Online and mobile banking included",
        "Automatic savings programs"
      ],
      startingBalance: "$100",
      monthlyFee: "$0",
      bgColor: "from-green-500 to-green-600",
      popular: true
    },
    {
      icon: Target,
      title: "Money Market",
      description: "Higher yields with easy access to your funds",
      features: [
        "Tiered interest rates up to 3.75% APY",
        "Check writing privileges",
        "Debit card access",
        "Higher balance requirements",
        "Premium customer service"
      ],
      startingBalance: "$2,500",
      monthlyFee: "$10*",
      bgColor: "from-purple-500 to-purple-600",
      popular: false
    },
    {
      icon: PiggyBank,
      title: "Certificate of Deposit",
      description: "Secure, guaranteed returns for your savings",
      features: [
        "Guaranteed interest rates",
        "Terms from 3 months to 5 years",
        "Penalty-free early withdrawal options",
        "Automatic renewal available",
        "FDIC insured"
      ],
      startingBalance: "$1,000",
      monthlyFee: "$0",
      bgColor: "from-orange-500 to-orange-600",
      popular: false
    }
  ];

  const creditCards = [
    {
              name: "Aurora Wealth Rewards Card",
      description: "Earn cashback on every purchase",
      features: [
        "2% cashback on all purchases",
        "No annual fee",
        "0% APR for first 15 months",
        "Fraud protection included"
      ],
      apr: "15.99% - 25.99%",
      annualFee: "$0"
    },
    {
              name: "Aurora Wealth Travel Card",
      description: "Perfect for travelers and adventurers",
      features: [
        "3x points on travel and dining",
        "No foreign transaction fees",
        "Travel insurance included",
        "Airport lounge access"
      ],
      apr: "17.99% - 27.99%",
      annualFee: "$95"
    }
  ];

  const loanTypes = [
    {
      icon: Home,
      title: "Home Loans",
      description: "Make homeownership a reality",
      rates: "Starting at 6.25% APR",
      features: ["First-time buyer programs", "Flexible terms", "Quick approval"]
    },
    {
      icon: Car,
      title: "Auto Loans",
      description: "Drive away with great rates",
      rates: "Starting at 4.99% APR",
      features: ["New and used vehicles", "Fast approval", "Flexible payments"]
    },
    {
      icon: GraduationCap,
      title: "Personal Loans",
      description: "Achieve your financial goals",
      rates: "Starting at 7.99% APR",
      features: ["Debt consolidation", "Home improvements", "No collateral required"]
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: "FDIC Insured",
      description: "Your deposits are protected up to $250,000"
    },
    {
      icon: Clock,
      title: "24/7 Banking",
      description: "Access your accounts anytime, anywhere"
    },
    {
      icon: Smartphone,
      title: "Mobile Banking",
      description: "Full-featured mobile app with mobile check deposit"
    },
    {
      icon: Award,
      title: "Award-Winning Service",
      description: "Recognized for excellence in customer satisfaction"
    }
  ];

  const testimonials = [
    {
      name: "Jennifer Martinez",
      role: "Teacher",
      content: "The high-yield savings account helped me save for my dream vacation. The rates are amazing!",
      rating: 5
    },
    {
      name: "David Thompson",
      role: "Small Business Owner",
      content: "Their personal banking services make managing my finances so much easier. Highly recommend!",
      rating: 5
    },
    {
      name: "Sarah Kim",
      role: "Nurse",
      content: "The mobile banking app is fantastic. I can deposit checks and transfer money instantly.",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative py-20 lg:py-32 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(59, 130, 246, 0.8), rgba(29, 78, 216, 0.8)), url(${businessPeopleImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-blue-800/90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Personal Banking
                <span className="block text-3xl md:text-5xl bg-gradient-to-r from-cyan-400 to-blue-300 bg-clip-text text-transparent">
                  That Works For You
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto mb-8">
                Discover banking solutions designed around your life. From everyday checking to high-yield savings, we help you achieve your financial goals.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-blue-900 bg-white rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <CreditCard className="w-6 h-6 mr-2" />
                Open account
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white border-2 border-white rounded-xl hover:bg-white hover:text-blue-900 transition-all duration-300"
              >
                Speak with Banker
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Account Types Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Choose Your Perfect Account
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Whether you're saving for the future or managing daily expenses, we have the right account for your needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {accountTypes.map((account, index) => (
              <motion.div
                key={account.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="relative group"
              >
                {account.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 h-full border-2 border-transparent hover:border-primary-200">
                  <div className={`w-16 h-16 bg-gradient-to-r ${account.bgColor} rounded-xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300`}>
                    <account.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{account.title}</h3>
                  <p className="text-gray-600 mb-4">{account.description}</p>
                  
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-500">Starting Balance:</span>
                      <span className="font-semibold text-gray-900">{account.startingBalance}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Monthly Fee:</span>
                      <span className="font-semibold text-gray-900">{account.monthlyFee}</span>
                    </div>
                  </div>

                  <ul className="space-y-2 mb-6">
                    {account.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/register"
                    className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white py-3 px-4 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all duration-300 font-semibold text-center block"
                  >
                    Open account
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Credit Cards Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Credit Cards That Reward You
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Earn rewards, build credit, and enjoy exclusive benefits with our premium credit card options
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {creditCards.map((card, index) => (
              <motion.div
                key={card.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-2xl p-8 border border-primary-200 hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{card.name}</h3>
                    <p className="text-gray-600">{card.description}</p>
                  </div>
                  <CreditCard className="w-12 h-12 text-primary-600" />
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <span className="text-sm text-gray-500">APR Range</span>
                    <div className="font-semibold text-gray-900">{card.apr}</div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Annual Fee</span>
                    <div className="font-semibold text-gray-900">{card.annualFee}</div>
                  </div>
                </div>

                <ul className="space-y-2 mb-6">
                  {card.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Link
                  to="/cards"
                  className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg hover:bg-primary-700 transition-colors duration-300 font-semibold text-center block"
                >
                  Apply Now
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Loans Section */}
      <section 
        className="py-20 relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(16, 185, 129, 0.9), rgba(5, 150, 105, 0.9)), url(${consultationImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Loans to Help You Achieve Your Dreams
            </h2>
            <p className="text-xl text-green-100 max-w-3xl mx-auto">
              From buying your first home to financing your dream car, we offer competitive rates and flexible terms
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {loanTypes.map((loan, index) => (
              <motion.div
                key={loan.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-colors duration-300"
              >
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-4">
                  <loan.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{loan.title}</h3>
                <p className="text-green-100 mb-3">{loan.description}</p>
                <div className="text-lg font-semibold text-white mb-4">{loan.rates}</div>
                <ul className="space-y-1 mb-6">
                  {loan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-green-100">
                      <CheckCircle className="w-3 h-3 text-green-300 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/loans"
                  className="w-full bg-white text-green-600 py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors duration-300 font-semibold text-center block"
                >
                  Learn More
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Personal Banking?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the benefits of community banking with modern convenience and personalized service
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section 
        className="py-20 relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(99, 102, 241, 0.9), rgba(79, 70, 229, 0.9)), url(${happyCustomersImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              What Our Customers Say
            </h2>
            <p className="text-xl text-indigo-100 max-w-3xl mx-auto">
                              Real stories from real customers who've made Aurora Wealth Reserves their banking home
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <blockquote className="text-white mb-4">
                  "{testimonial.content}"
                </blockquote>
                <div>
                  <div className="font-semibold text-white">{testimonial.name}</div>
                  <div className="text-sm text-indigo-200">{testimonial.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20 relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(245, 101, 101, 0.9), rgba(220, 38, 38, 0.9)), url(${familyPlanningImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Start Your Banking Journey?
            </h2>
            <p className="text-xl text-red-100 mb-8">
                              Join thousands of satisfied customers who've chosen Aurora Wealth Reserves for their personal banking needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-red-600 bg-white rounded-xl hover:bg-gray-50 transition-colors duration-300 shadow-lg hover:shadow-xl"
              >
                <CreditCard className="w-6 h-6 mr-2" />
                Open account
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white border-2 border-white rounded-xl hover:bg-white hover:text-red-600 transition-colors duration-300"
              >
                <Users className="w-6 h-6 mr-2" />
                Schedule Consultation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PersonalBanking; 